import React, { useState, useEffect, useCallback } from 'react';
import { Bus, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { AdminCharterAPI } from '../../services/api';
import { CHARTER_STATUS_LABELS, formatRand, formatDateTime, apiErrorMessage } from './charterUtils';

const STATUS_STYLES = {
  none: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const ItemIcon = ({ ok }) => {
  if (ok === true) return <CheckCircle size={16} className="text-green-600 mt-0.5 shrink-0" />;
  if (ok === false) return <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />;
  return <AlertCircle size={16} className="text-yellow-500 mt-0.5 shrink-0" />;
};

/**
 * Companies > detail, the Charter block (spec 6.4, 5.8): the operator's
 * charter status, the readiness checklist, "Approve charter" (enabled only
 * when the checklist passes and admin has confirmed the by-eye items) and
 * "Reject with reason", which also revokes an approved operator. The
 * operator status controls above it are untouched (R47, R77).
 */
const CompanyCharterBlock = ({ companyId }) => {
  const [readiness, setReadiness] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [note, setNote] = useState('');
  const [amountChecked, setAmountChecked] = useState(false);
  const [areaChecked, setAreaChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await AdminCharterAPI.getReadiness(companyId);
      setReadiness(res.data.data);
      setLoadError('');
    } catch (e) {
      setLoadError(apiErrorMessage(e, 'Could not load the charter readiness'));
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    setBusy(true);
    setError('');
    try {
      await AdminCharterAPI.decide(companyId, decision, note.trim() || undefined);
      setNote('');
      setAmountChecked(false);
      setAreaChecked(false);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save the decision'));
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center"><Bus size={18} className="mr-2" />Charter</h2>
        <p className="mt-2 text-sm text-red-600">{loadError}</p>
      </div>
    );
  }
  if (!readiness) {
    return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 text-sm text-gray-500">Loading charter...</div>;
  }

  const status = readiness.charter.status;
  const automatic = readiness.checklist.filter((i) => !i.manual);
  const manual = readiness.checklist.find((i) => i.key === 'insuranceAmount');
  const canApprove = status === 'pending' && readiness.canApprove && amountChecked && areaChecked;
  const canReject = status === 'pending' || status === 'approved';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-testid="charter-block">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center"><Bus size={18} className="mr-2" />Charter</h2>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.none}`}>
          {CHARTER_STATUS_LABELS[status] || status}
        </span>
      </div>
      <div className="mt-1 text-xs text-gray-500 space-x-3">
        {readiness.charter.appliedAt && <span>Applied {formatDateTime(readiness.charter.appliedAt)}</span>}
        {readiness.charter.decidedAt && <span>Decided {formatDateTime(readiness.charter.decidedAt)}</span>}
      </div>
      {readiness.charter.note && <p className="mt-2 text-sm text-gray-700">Note: {readiness.charter.note}</p>}

      <h3 className="mt-4 text-sm font-medium text-gray-700">Readiness</h3>
      <ul className="mt-2 space-y-2">
        {automatic.map((item) => (
          <li key={item.key} className="flex items-start gap-2 text-sm">
            <ItemIcon ok={item.ok} />
            <span>
              <span className="text-gray-900">{item.label}</span>
              {item.detail && <span className="block text-xs text-gray-500">{item.detail}</span>}
            </span>
          </li>
        ))}
      </ul>

      {status === 'pending' && (
        <div className="mt-4 space-y-2 text-sm">
          <h3 className="font-medium text-gray-700">Check by eye before approving</h3>
          {manual && (
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={amountChecked} onChange={(e) => setAmountChecked(e.target.checked)} className="mt-1" />
              <span>
                {manual.label}.{' '}
                <span className="text-gray-500">
                  Insured {manual.detail.insuredAmountCents ? formatRand(manual.detail.insuredAmountCents) : 'amount not recorded'};
                  {' '}minimum {formatRand(manual.detail.requiredCents)}.
                </span>
              </span>
            </label>
          )}
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={areaChecked} onChange={(e) => setAreaChecked(e.target.checked)} className="mt-1" />
            <span>The authorised area on each licence covers the operator's service areas.</span>
          </label>
          <AreaComparison readiness={readiness} />
        </div>
      )}

      <ResourceRows readiness={readiness} />

      {canReject && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label htmlFor="charter-note" className="block text-sm font-medium text-gray-700 mb-1">
              Reason (required to reject or revoke, shown to the operator)
            </label>
            <input
              id="charter-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="e.g. Insurance expires before most hires"
            />
          </div>
          {status === 'pending' && (
            <button
              onClick={() => decide('approve')}
              disabled={busy || !canApprove}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm"
            >
              Approve charter
            </button>
          )}
          <button
            onClick={() => decide('reject')}
            disabled={busy || !note.trim()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm"
          >
            {status === 'approved' ? 'Revoke charter' : 'Reject with reason'}
          </button>
        </div>
      )}
      {status === 'none' && <p className="mt-4 text-sm text-gray-500">The operator has not applied for charter yet.</p>}
      {status === 'rejected' && <p className="mt-4 text-sm text-gray-500">The operator must apply again before charter can be approved.</p>}
      {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
};

/**
 * What the area tick compares (spec 5.8): the authorised area recorded on each
 * counting licence beside the operator's service areas. There is no register,
 * so admin judges the match.
 */
const AreaComparison = ({ readiness }) => {
  const areas = readiness.operationalAreas || [];
  const licensed = (readiness.vehicles || []).filter((v) => v.eligible);
  return (
    <div className="ml-6 text-xs text-gray-600 space-y-1" data-testid="area-comparison">
      <div>
        <span className="font-medium text-gray-700">Operator's areas: </span>
        {areas.length ? areas.join(', ') : 'none set'}
      </div>
      <div>
        <span className="font-medium text-gray-700">Licence areas: </span>
        {licensed.length === 0 && 'no counting licence yet'}
      </div>
      {licensed.length > 0 && (
        <ul className="list-disc list-inside">
          {licensed.map((v) => (
            <li key={v._id}>{v.licensePlate || 'No plate'}: {v.authorisedAreaText || 'area not recorded'}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Each vehicle and driver with why it does or does not count. */
const ResourceRows = ({ readiness }) => {
  const vehicles = readiness.vehicles || [];
  const drivers = readiness.drivers || [];
  if (!vehicles.length && !drivers.length) return null;
  const row = (key, name, eligible, message) => (
    <li key={key} className="flex items-start gap-2">
      <ItemIcon ok={Boolean(eligible)} />
      <span>{name}{!eligible && message ? <span className="text-gray-500">: {message}</span> : null}</span>
    </li>
  );
  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div>
        <h3 className="font-medium text-gray-700 mb-1">Vehicles</h3>
        <ul className="space-y-1">
          {vehicles.map((v) => row(v._id, `${[v.vehicleMake, v.model].filter(Boolean).join(' ')} ${v.licensePlate || ''} (${v.charterClass || 'no class'})`, v.eligible, v.message))}
        </ul>
      </div>
      <div>
        <h3 className="font-medium text-gray-700 mb-1">Drivers</h3>
        <ul className="space-y-1">
          {drivers.map((d) => row(d._id, `${d.name}${d.readyClasses && d.readyClasses.length ? ` (${d.readyClasses.join(', ')})` : ''}`, d.eligible, d.message))}
        </ul>
      </div>
    </div>
  );
};

export default CompanyCharterBlock;
